import {
	cn,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@babybjorn/ui";
import {
	type ISbStoryData,
	type StoryblokRichTextNode,
	type StoryblokRichTextOptions,
	BlockTypes,
	convertAttributesInElement,
	MarkTypes,
	richTextResolver,
} from "@storyblok/react";
import { type FC, type ReactElement, createElement, Fragment } from "react";
import Link from "../Link";
import * as s from "./RichText.module.scss";

type RichTextProps = {
	content?: StoryblokRichTextNode<ReactElement> | string | null;
	className?: string;
	centeredText?: boolean | null;
};

type BasicLink = {
	linktype?: string | undefined;
	href?: string | undefined;
	target?: string | undefined;
	anchor?: string | undefined;
	uuid?: string | undefined;
};

type StoryLink = BasicLink & {
	story: ISbStoryData;
};

type StoryblokMark = {
	attrs?: {
		class?: string;
		id?: string;
	};
};

const isStoryLink = (link: BasicLink): link is StoryLink =>
	link.linktype === "story" && "story" in link;

const addHttpsToUrl = (url: string) =>
	/^https?:\/\//i.test(url) ? url : `https://${url}`;

const RichText: FC<RichTextProps> = ({ content, className, centeredText }) => {
	let keyCounter = 0;
	const contentHash =
		typeof content === "string" ? content.slice(0, 10) : "rich";

	const generateKey = (prefix: string): string => {
		keyCounter += 1;
		return `${contentHash}-${prefix}-${keyCounter}`;
	};
	if (!content) return null;
	if (typeof content === "string") {
		return (
			<p
				className={cn(s.richtext, className, {
					[s.centeredText]: centeredText,
				})}
			>
				{content}
			</p>
		);
	}

	const options: StoryblokRichTextOptions<ReactElement> = {
		renderFn: createElement,
		keyedResolvers: true,
		textFn: (text: string, attrs?: Record<string, unknown>) => {
			return createElement(Fragment, attrs, text);
		},
		resolvers: {
			[MarkTypes.LINK]: (node: StoryblokRichTextNode<ReactElement>) => {
				const link = node.attrs as BasicLink;
				const { href, target, anchor, linktype } = link;

				const translatedSlug = isStoryLink(link)
					? link.story.full_slug
					: undefined;

				const fixedHref =
					linktype === "url" && target === "_blank"
						? addHttpsToUrl(translatedSlug ?? href ?? "")
						: (translatedSlug ?? href ?? "");

				return (
					<Link
						key={generateKey("link")}
						anchor={anchor}
						href={fixedHref}
						linktype={linktype}
						target={target}
					>
						{node.text}
					</Link>
				);
			},
			[BlockTypes.TABLE]: (node: StoryblokRichTextNode<ReactElement>) => {
				const body = (
					<TableBody key={generateKey("table-body")}>{node.children}</TableBody>
				);

				return <Table key={generateKey("table")}>{body}</Table>;
			},
			[BlockTypes.TABLE_ROW]: (node: StoryblokRichTextNode<ReactElement>) => {
				const { ...rest } = node.attrs || {};

				return (
					<TableRow {...rest} key={generateKey("table-row")}>
						{node.children}
					</TableRow>
				);
			},
			[BlockTypes.TABLE_CELL]: (node: StoryblokRichTextNode<ReactElement>) => {
				const { className, ...rest } = node.attrs || {};

				return (
					<TableCell
						className={cn(
							"min-w-30",
							"[&>*]:flex [&>*]:flex-col [&>*]:justify-center",
							"[&_img]:w-40 md:[&_img]:w-60 [&>*]:p-2",
							className,
						)}
						{...rest}
						key={generateKey("table-cell")}
					>
						{node.children}
					</TableCell>
				);
			},
			[BlockTypes.TABLE_HEADER]: (
				node: StoryblokRichTextNode<ReactElement>,
			): ReactElement => {
				const { ...rest } = node.attrs || {};

				return (
					<TableHead
						className={cn({ "text-center": node.attrs?.colspan > 1 })}
						{...rest}
						key={generateKey("table-header")}
					>
						{node.children}
					</TableHead>
				);
			},
			[BlockTypes.HEADING]: (
				node: StoryblokRichTextNode<ReactElement>,
			): ReactElement => {
				const level = node.attrs?.level as number | undefined;

				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				if (!node.content || !Array.isArray(node.content)) {
					return level ? createElement(`h${level}`) : <div />;
				}

				const text = node.content.map((child) => child.text?.trim()).join(" ");

				if (!level) return <div>{text}</div>;

				const result = node.content.reduce<{
					anchor?: string;
					classNames: string[];
				}>(
					(acc, child) => {
						// @ts-expect-error - Type is wrong, `child.marks` exists
						const marks = child.marks as StoryblokMark[] | undefined;
						if (!marks) return acc;

						for (const mark of marks) {
							if (mark.attrs?.id && !acc.anchor) {
								acc.anchor = mark.attrs.id;
							}
							if (mark.attrs?.class) {
								acc.classNames.push("!" + mark.attrs.class);
							}
						}

						return acc;
					},
					{ anchor: undefined, classNames: [] },
				);

				const anchor = result.anchor;
				const className = result.classNames.join(" ");

				const Tag = `h${level}` as keyof JSX.IntrinsicElements;

				return (
					<Tag
						id={anchor}
						key={generateKey("heading")}
						className={cn("mt-4 first:mt-0", className)}
					>
						{text}
					</Tag>
				);
			},
		},
	};

	const html = richTextResolver<ReactElement>(options).render(content);
	const formattedHtml = convertAttributesInElement(html);

	return (
		<div
			className={cn(s.richtext, className, {
				[s.centeredText]: centeredText,
			})}
		>
			{formattedHtml}
		</div>
	);
};

export default RichText;
